<template>
    <v-card elevation="0">
        <v-card-title>
            <span>
                {{ `${$t('reviews_and_feedback')}` }}
            </span>
        </v-card-title>
        <v-card-text>
            <v-expansion-panels>
                <v-expansion-panel>
                    <v-expansion-panel-title class="pl-1 width-auto text-primary text-capitalize">
                        {{ $t('filters') }}
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <v-row class="py-4">
                            <v-col md="8" cols="12" class="filters-form">
                                <v-row>
                                    <v-col md="6" cols="12">
                                        <order-service-daterange
                                            :start-date-label="$t('travel_date_from')"
                                            :end-date-label="$t('travel_date_to')"
                                            :outlined="false"
                                            :single-line="false"
                                            :no-title="true"
                                            clearable
                                            dense
                                            :_start-date="filters.travelDateFrom"
                                            :_end-date="filters.travelDateTo"
                                            @change-start-date="filters.travelDateFrom = $event"
                                            @change-end-date="filters.travelDateTo = $event"
                                        />
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <v-select
                                            v-model="filters.status"
                                            :items="statuses"
                                            :label="$t('status')"
                                            clearable
                                            :item-title="'title'"
                                            :item-value="'value'"
                                            density="compact"
                                            hide-details
                                        />
                                    </v-col>
                                    <v-col md="6" cols="12">
                                        <order-service-daterange
                                            :start-date-label="$t('created_date_from')"
                                            :end-date-label="$t('created_date_to')"
                                            :outlined="false"
                                            :single-line="false"
                                            :no-title="true"
                                            clearable
                                            dense
                                            :_start-date="filters.createdDateFrom"
                                            :_end-date="filters.createdDateTo"
                                            @change-start-date="filters.createdDateFrom = $event"
                                            @change-end-date="filters.createdDateTo = $event"
                                        />
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <private-client-and-companies-autocomplete
                                            :show-icon="false"
                                            :label="$t('client_name')"
                                            :relationship="['CLIENT']"
                                            dense
                                            @change="onClientNameFilterChange"
                                        />
                                    </v-col>
                                    <v-col md="6" cols="12">
                                        <order-service-daterange
                                            :start-date-label="$t('modified_date_from')"
                                            :end-date-label="$t('modified_date_to')"
                                            :outlined="false"
                                            :single-line="false"
                                            :no-title="true"
                                            clearable
                                            dense
                                            :_start-date="filters.modifiedDateFrom"
                                            :_end-date="filters.modifiedDateTo"
                                            @change-start-date="filters.modifiedDateFrom = $event"
                                            @change-end-date="filters.modifiedDateTo = $event"
                                        />
                                    </v-col>
                                    <v-col cols="6" md="3">
                                        <v-text-field
                                            v-model="filters.serviceName"
                                            :label="$t('service_name')"
                                            density="compact"
                                            hide-details
                                        />
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-spacer />
                            <v-col md="2" cols="12">
                                <v-row>
                                    <v-col cols="6">
                                        <v-btn
                                            block
                                            :color="isSomeFiltersSelected ? 'primary' : 'white'"
                                            size="small"
                                            @click="getFeedbacks"
                                        >
                                            {{ $t('filter') }}
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-btn color="white" block size="small" @click="resetFilters">
                                            {{ $t('reset') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <v-data-table
                v-if="feedbacks"
                :headers="headers"
                :sort-by="[{key: 'created', order: 'desc'}]"
                :disable-sort="isMobile"
                :items="feedbacks"
                :loading="feedbacksLoading"
            >
                <template v-slot:item.mobile="{item}">
                    <div class="py-2 d-flex">
                        <div
                            class="d-flex flex-column flex-grow-1 flex-shrink-1 overflow-hidden align-start"
                            style="text-align: left"
                        >
                            <nuxt-link
                                class="mb-1"
                                no-prefetch
                                :to="{name: 'order-details', params: {id: +item.reservationNumber}}"
                                @click.native.stop
                            >
                                <strong>{{ +item.reservationNumber }}</strong>
                            </nuxt-link>
                            <div class="d-flex flex-column mb-2">
                                <span v-if="item.serviceName" class="font-weight-bold">
                                    {{ item.serviceName }}
                                </span>
                                <span v-if="item.clientName" class="font-weight-medium">
                                    {{ item.clientName }}
                                </span>
                                <span v-if="item.created" class="text--secondary">
                                    {{ $filters.dateTimeFormat(item.created) }}
                                </span>
                            </div>
                            <div v-if="item.positiveFeedback" class="text-black">
                                <span class="text-success me-1 font-weight-bold">+</span>
                                <span>
                                    {{ item.positiveFeedback }}
                                </span>
                            </div>
                            <div v-if="item.negativeFeedback" class="text-black">
                                <span
                                    class="text-error me-1 font-weight-bold"
                                    style="padding-left: 1px; padding-right: 1px"
                                    >-</span
                                >
                                <span>
                                    {{ item.negativeFeedback }}
                                </span>
                            </div>
                        </div>
                        <div class="d-flex flex-column flex-grow-0 flex-shrink-0 align-end overflow-hidden">
                            <v-chip
                                v-if="item.reviewScore"
                                :color="getColor(1 - item.reviewScore / 10)"
                                label
                                size="x-small"
                                style="margin-top: 4px"
                                class="me-1 mb-1"
                            >
                                <span class="text-caption text-white">
                                    {{ item.reviewScore }}
                                </span>
                            </v-chip>
                            <div v-if="item.id !== feedbackStatusEditId" class="d-flex align-center">
                                <span>
                                    {{ $t(`review_status.${item.status}`) }}
                                </span>
                                <v-btn
                                    size="small"
                                    icon="mdi-pencil"
                                    variant="text"
                                    @click="feedbackStatusEditId = item.id"
                                />
                            </div>
                            <div v-else style="width: 150px" class="d-flex align-center">
                                <v-select
                                    density="compact"
                                    item-title="text"
                                    item-value="value"
                                    :items="statuses"
                                    variant="outlined"
                                    hide-details
                                    :disabled="item.id === feedbackStatusLoadingId"
                                    :loading="item.id === feedbackStatusLoadingId"
                                    :model-value="item.status"
                                    @update:model-value="changeStatus(item, $event)"
                                />
                            </div>
                        </div>
                    </div>
                </template>
                <template v-slot:item.created="{item}">
                    {{ $filters.dateTimeFormat(item.created) }}
                </template>
                <template v-slot:item.serviceName="{item}">
                    <span class="font-weight-bold">
                        {{ item.serviceName }}
                    </span>
                    <span>
                        {{ `(${item.cityName}, ${item.countryName})` }}
                    </span>
                </template>
                <template v-slot:item.canRecommend="{item}">
                    {{ item.canRecommend ? $t('yes') : $t('no') }}
                </template>
                <template v-slot:item.startServiceDate="{item}">
                    {{ $filters.dateShortFormat(item.startServiceDate) }} -
                    {{ $filters.dateShortFormat(item.endServiceDate) }}
                </template>
                <template v-slot:item.status="{item}">
                    <div v-if="item.id !== feedbackStatusEditId" class="d-flex align-center">
                        <span>
                            {{ $t(`review_status.${item.status}`) }}
                        </span>
                        <v-btn size="small" icon="mdi-pencil" variant="text" @click="feedbackStatusEditId = item.id" />
                    </div>
                    <div v-else style="width: 150px" class="d-flex align-center">
                        <v-select
                            density="compact"
                            item-title="title"
                            item-value="value"
                            :items="statuses"
                            variant="outlined"
                            hide-details
                            :disabled="item.id === feedbackStatusLoadingId"
                            :loading="item.id === feedbackStatusLoadingId"
                            :model-value="item.status"
                            @update:model-value="changeStatus(item, $event)"
                        />
                    </div>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script lang="ts">
    import {Component, Vue, Watch} from 'vue-facing-decorator'
    import OrderServiceDaterange from '@/components/account/filters/OrderServiceDaterange.vue'
    import PrivateClientAndCompaniesAutocomplete from '@/components/search/forms/PrivateClientAndCompaniesAutocomplete.vue'
    import OrdersPriceLabel from '@/components/account/snippets/OrdersPriceLabel.vue'

    @Component({
        components: {OrdersPriceLabel, PrivateClientAndCompaniesAutocomplete, OrderServiceDaterange},
    })
    export class FeedbackList extends Vue {
        feedbacks = []
        feedbacksLoading = false
        feedbackStatusLoadingId = null
        feedbackStatusEditId = null

        filters = {}

        @Watch('filters', {deep: true})
        onFiltersChange() {
            Object.keys(this.filters).forEach(key =>
                this.filters[key] === undefined || this.filters[key] === null || this.filters[key] === ''
                    ? // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                      delete this.filters[key]
                    : {}
            )
        }

        async mounted() {
            await this.getFeedbacks()
        }

        async getFeedbacks() {
            try {
                this.feedbacksLoading = true
                this.feedbacks = (
                    await this.$api.reviews.get({
                        ...this.filters,
                    })
                ).reviews

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.feedbacksLoading = false
            }
        }

        async changeStatus(item, newStatus) {
            try {
                this.feedbackStatusLoadingId = item.id
                const rs = await this.$api.reviews.put(item.id, {
                    status: newStatus,
                })
                item.status = rs.status
                this.feedbackStatusEditId = null
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.feedbackStatusLoadingId = null
            }
        }

        onClientNameFilterChange(val) {
            this.filters = {...this.filters, clientId: val?.personId}
        }

        resetFilters() {
            this.filters = {}
            this.getFeedbacks()
        }

        get headers() {
            if (this.isMobile) {
                return [{value: 'mobile'}]
            }

            return [
                {title: this.$t('date_time'), value: 'created'},
                {title: this.$t('client'), value: 'clientName'},
                {title: this.$t('service'), value: 'serviceName'},
                {title: this.$t('review_score'), value: 'reviewScore', align: 'center'},
                {title: this.$t('would_recommend'), value: 'canRecommend', align: 'center'},
                {title: this.$t('positive_feedback'), value: 'positiveFeedback', align: 'center', sortable: false},
                {title: this.$t('negative_feedback'), value: 'negativeFeedback', align: 'center', sortable: false},
                {title: this.$t('travel_dates'), value: 'startServiceDate'},
                {title: this.$t('order_number'), value: 'reservationNumber'},
                {title: this.$t('status'), value: 'status'},
            ]
        }

        get statuses() {
            return [
                {value: 'DECLINED', title: this.$t('review_status.DECLINED')},
                {value: 'ON_REVIEW', title: this.$t('review_status.ON_REVIEW')},
                {value: 'PUBLISHED', title: this.$t('review_status.PUBLISHED')},
            ]
        }

        get isSomeFiltersSelected() {
            return Object.keys(this.filters).length !== 0
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        getColor(value) {
            const hue = ((1 - value) * 120).toString(10)
            return ['hsl(', hue, ',100%, 30%)'].join('')
        }
    }

    export default FeedbackList
</script>

<style scoped lang="scss">
    @media (max-width: 960px) {
        :deep(.v-data-table__mobile-row__cell) {
            width: 100%;
        }

        :deep(.v-data-table__mobile-row) {
            padding: 0 !important;
        }
    }
</style>
